import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Film, Settings as SettingsIcon, ExternalLink, History as HistoryIcon, CalendarDays, AlertTriangle } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, end: false },
  { to: "/clips", label: "Clips", icon: Film, end: false },
  { to: "/history", label: "History", icon: HistoryIcon, end: false },
  { to: "/settings", label: "Settings", icon: SettingsIcon, end: false },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReauthBanner() {
  const [stale, setStale] = useState([]);

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/integrations/summary`);
        if (!alive) return;
        const bad = Object.entries(r.data || {})
          .filter(([_, v]) => v && v.needs_reauth)
          .map(([k]) => k);
        setStale(bad);
      } catch (_) {
        /* silent — banner is a nice-to-have */
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60_000); // re-check every 60s
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (stale.length === 0) return null;
  const label = stale.length === 1
    ? `${stale[0][0].toUpperCase()}${stale[0].slice(1)} needs re-auth`
    : `${stale.length} integrations need re-auth`;

  return (
    <div
      data-testid="reauth-banner"
      className="flex items-center justify-between gap-3 border-b border-amber-500/30 bg-amber-500/10 px-5 py-2.5 text-xs text-amber-200"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4" />
        <span>
          <strong className="font-medium">{label}.</strong>{" "}
          Scheduled posts to this platform will fail until you reconnect.
        </span>
      </div>
      <NavLink
        to="/"
        data-testid="reauth-banner-link"
        className="rounded bg-amber-500/20 px-2.5 py-1 font-medium text-amber-100 ring-1 ring-amber-500/40 hover:bg-amber-500/30"
      >
        Open Dashboard →
      </NavLink>
    </div>
  );
}

export default function Layout({ children }) {
  const loc = useLocation();
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden w-60 shrink-0 border-r border-zinc-900 bg-black/40 lg:flex lg:flex-col" data-testid="sidebar">
          <div className="border-b border-zinc-900 px-6 py-5">
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">True Believer Fam</p>
            <h1 className="mt-1 text-lg font-semibold tracking-tight">
              Auto<span className="text-amber-400">Clipper</span>
            </h1>
          </div>
          <nav className="flex flex-1 flex-col gap-1 p-3" data-testid="sidebar-nav">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive ? "bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/30" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-zinc-900 p-4 text-[11px] text-zinc-600">
            <a className="inline-flex items-center gap-1 hover:text-zinc-400" href="/api/openapi.json" target="_blank" rel="noreferrer">
              OpenAPI <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">
          <ReauthBanner />
          {/* Mobile header */}
          <div className="flex items-center justify-between border-b border-zinc-900 bg-black/30 px-5 py-3 lg:hidden">
            <h1 className="text-sm font-medium">
              True Believer Fam <span className="text-amber-400">AutoClipper</span>
            </h1>
            <nav className="flex gap-3 text-xs">
              {NAV.map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => (isActive ? "text-amber-300" : "text-zinc-500")}
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div key={loc.pathname} className="px-6 py-8 lg:px-10 lg:py-10">{children}</div>
        </main>
      </div>
    </div>
  );
}
